import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { displayProtocolTag } from '../utils/protocol';

const API_URL = 'https://reportfius-backend.up.railway.app';

const SuperSimpleDashboard = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    users: 0,
    forms: 0,
    complaints: 0
  });

  // Buscar estatísticas ao carregar
  useEffect(() => {
    fetchStats();
  }, []);

  // Função para buscar estatísticas
  const fetchStats = async () => {
    try {
      // Buscar contadores
      const [usersRes, formsRes, complaintsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`, {
          withCredentials: true
        }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API_URL}/api/forms`, {
          withCredentials: true
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/complaints/analytics/list`, {
          withCredentials: true
        }).catch(() => ({ data: { complaints: [] } }))
      ]);

      setStats({
        users: usersRes.data?.users?.length || 0,
        forms: Array.isArray(formsRes.data) ? formsRes.data.length : (formsRes.data?.forms?.length || 0),
        complaints: complaintsRes.data?.complaints?.length || 0
      });
    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
    }
  };

  // Função genérica para buscar dados
  const fetchData = async (endpoint) => {
    setLoading(true);
    setError(null);
    setData(null);
    
    try {
      const response = await axios.get(`${API_URL}${endpoint}`, {
        withCredentials: true
      });
      
      console.log(`✅ Dados de ${endpoint}:`, response.data);
      setData(response.data);
    } catch (err) {
      console.error('❌ Erro:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal e buscar dados
  const openModal = (type) => {
    setActiveModal(type);
    
    switch(type) {
      case 'users':
        fetchData('/api/users');
        break;
      case 'forms':
        fetchData('/api/forms');
        break;
      case 'complaints':
        fetchData('/api/complaints/analytics/list');
        break;
      default:
        break;
    }
  };

  // Modal Component MELHORADO
  const Modal = ({ title, onClose, children }) => (
    <div className="rf-modal">
      <div className="rf-modal__panel rf-modal__panel--wide">
        <div className="rf-modal__head rf-modal__head--brand">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            {title === 'Usuários' && '👥'}
            {title === 'Formulários' && '📝'}
            {title === 'Denúncias' && '📢'}
            {title}
          </h2>
          <button 
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {children}
        </div>
      </div>
    </div>
  );

  // Renderizar conteúdo do modal baseado no tipo
  const renderModalContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando dados...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-red-700 font-medium">Erro ao carregar dados</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="mt-2 text-gray-500">Nenhum dado disponível</p>
        </div>
      );
    }

    switch(activeModal) {
      case 'users':
        const users = data.users || [];
        return (
          <div>
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-2xl font-bold text-blue-900">{users.length}</p>
              <p className="text-blue-700">Usuários cadastrados</p>
            </div>
            <div className="grid gap-4">
              {users.map(user => (
                <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-lg text-gray-900">{user.full_name || user.username}</p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                          user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'client_admin' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">{user.email || 'Sem email'}</p>
                      {user.client_name && (
                        <p className="text-sm text-gray-500 mt-2">
                          <span className="font-medium">Cliente:</span> {user.client_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active !== false ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'forms':
        const forms = Array.isArray(data) ? data : (data.data || data.forms || []);
        return (
          <div>
            <div className="bg-purple-50 rounded-lg p-4 mb-6">
              <p className="text-2xl font-bold text-purple-900">{forms.length}</p>
              <p className="text-purple-700">Formulários criados</p>
            </div>
            <div className="grid gap-4">
              {forms.map(form => (
                <div key={form.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-lg text-gray-900">{form.title}</p>
                      <p className="text-gray-600 mt-1">{form.description || 'Sem descrição'}</p>
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-sm text-gray-500">
                          <span className="font-medium">Cliente:</span> {form.client_name || form.client_slug}
                        </span>
                        {form.fields && (
                          <span className="text-sm text-gray-500">
                            <span className="font-medium">Campos:</span> {JSON.parse(form.fields).length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        form.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {form.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'complaints':
        const complaints = data.complaints || [];
        return (
          <div>
            <div className="bg-orange-50 rounded-lg p-4 mb-6">
              <p className="text-2xl font-bold text-orange-900">{complaints.length}</p>
              <p className="text-orange-700">Denúncias recebidas</p>
            </div>
            <div className="grid gap-4">
              {complaints.map(complaint => (
                <div key={complaint.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">Denúncia {displayProtocolTag(complaint)}</p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          complaint.status === 'resolved' ? 'bg-green-100 text-green-800' : 
                          complaint.status === 'investigating' ? 'bg-yellow-100 text-yellow-800' :
                          complaint.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {complaint.status === 'resolved' ? 'Resolvida' :
                           complaint.status === 'investigating' ? 'Investigando' :
                           complaint.status === 'pending' ? 'Pendente' :
                           complaint.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">
                        {complaint.form_title || 'Formulário não identificado'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span>📅 {new Date(complaint.created_at).toLocaleDateString('pt-BR')}</span>
                        {complaint.client_name && <span>🏢 {complaint.client_name}</span>}
                        {complaint.message_count > 0 && <span>💬 {complaint.message_count} mensagens</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header MELHORADO */}
      <header className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Dashboard Simplificado
              </h1>
              <p className="text-gray-600 mt-1">Acesso rápido às informações principais</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => window.location.href = '/admin/dashboard'}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Dashboard Completo
              </button>
              <button 
                onClick={() => {
                  void axios.post('/api/auth/logout', {}, { withCredentials: true }).catch(() => {});
                  window.location.href = '/admin/login';
                }}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Cards de Ação MELHORADOS */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card Usuários */}
          <div 
            onClick={() => openModal('users')}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl cursor-pointer transition-all transform hover:scale-105 overflow-hidden"
          >
            <div className="rf-modal__head rf-modal__head--brand">
              <div className="text-5xl mb-3">👥</div>
              <h3 className="text-2xl font-bold mb-1">Usuários</h3>
              <p className="text-blue-100">Gerenciar usuários do sistema</p>
            </div>
            <div className="p-6 bg-blue-50 group-hover:bg-blue-100 transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-blue-900">{stats.users}</span>
                <span className="text-blue-600">Ver todos →</span>
              </div>
            </div>
          </div>

          {/* Card Formulários */}
          <div 
            onClick={() => openModal('forms')}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl cursor-pointer transition-all transform hover:scale-105 overflow-hidden"
          >
            <div className="rf-modal__head rf-modal__head--brand">
              <div className="text-5xl mb-3">📝</div>
              <h3 className="text-2xl font-bold mb-1">Formulários</h3>
              <p className="text-purple-100">Modelos de denúncia</p>
            </div>
            <div className="p-6 bg-purple-50 group-hover:bg-purple-100 transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-purple-900">{stats.forms}</span>
                <span className="text-purple-600">Ver todos →</span>
              </div>
            </div>
          </div>

          {/* Card Denúncias */}
          <div 
            onClick={() => openModal('complaints')}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl cursor-pointer transition-all transform hover:scale-105 overflow-hidden"
          >
            <div className="rf-modal__head rf-modal__head--brand">
              <div className="text-5xl mb-3">📢</div>
              <h3 className="text-2xl font-bold mb-1">Denúncias</h3>
              <p className="text-orange-100">Denúncias recebidas</p>
            </div>
            <div className="p-6 bg-orange-50 group-hover:bg-orange-100 transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-orange-900">{stats.complaints}</span>
                <span className="text-orange-600">Ver todas →</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cards de Features Avançadas */}
        <div className="mt-8 space-y-6">
          {/* CARD ESPECIAL - AI INTELLIGENCE HUB */}
          <div 
            onClick={() => window.location.href = '/analytics-disruptive'}
            className="group relative bg-black text-white p-8 rounded-2xl shadow-2xl hover:shadow-[0_0_50px_rgba(6,182,212,0.5)] cursor-pointer transition-all transform hover:scale-[1.02] overflow-hidden"
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 opacity-90"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10 animate-pulse"></div>
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-6xl animate-pulse">🧠</div>
                  <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                    NOVO! EXCLUSIVO!
                  </div>
                </div>
                <h3 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
                  AI Intelligence Hub
                </h3>
                <p className="text-xl opacity-90 mb-4">
                  Sistema de IA com Machine Learning que DESTRÓI a concorrência!
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Previsões com 97.3% de precisão</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Insights automáticos em tempo real</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Risk Score com análise preditiva</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Ações automatizadas pela IA</span>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-bold">
                    🤖 Machine Learning
                  </span>
                  <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-bold">
                    📊 Análise Preditiva
                  </span>
                  <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-bold">
                    ⚡ Real-time
                  </span>
                  <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-bold">
                    🎯 97.3% Precisão
                  </span>
                </div>
              </div>
              <div className="text-8xl opacity-50 group-hover:opacity-100 transition-all group-hover:translate-x-2">
                →
              </div>
            </div>
            
            {/* Animated particles */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`
                  }}
                />
              ))}
            </div>
          </div>

          {/* Card Sistema de Investigação */}
          <div 
            onClick={() => window.location.href = '/investigation'}
            className="group bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl cursor-pointer transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-3xl font-bold mb-2">Sistema de Investigação</h3>
                <p className="text-xl opacity-90">Kanban interativo com drag-and-drop, timeline e gestão de evidências</p>
                <div className="flex gap-4 mt-4">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📋 Kanban Board</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📁 Evidências</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">⏱️ Timeline</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">✅ Tasks</span>
                </div>
              </div>
              <div className="text-6xl opacity-50 group-hover:opacity-100 transition-opacity">
                →
              </div>
            </div>
          </div>

          {/* Card Analytics MELHORADO */}
          <div 
            onClick={() => window.location.href = '/analytics-disruptive'}
            className="group bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl cursor-pointer transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-3xl font-bold mb-2">Analytics Disruptivo</h3>
                <p className="text-xl opacity-90">Dashboard interativo com gráficos avançados e análises em tempo real</p>
                <div className="flex gap-4 mt-4">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📈 Gráficos</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">🎯 KPIs</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📉 Tendências</span>
                </div>
              </div>
              <div className="text-6xl opacity-50 group-hover:opacity-100 transition-opacity">
                →
              </div>
            </div>
          </div>

          {/* Card Dashboard Moderno */}
          <div 
            onClick={() => window.location.href = '/admin/modern'}
            className="group bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl cursor-pointer transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-6xl mb-4">🎨</div>
                <h3 className="text-3xl font-bold mb-2">Dashboard Moderno</h3>
                <p className="text-xl opacity-90">Interface moderna com design system avançado e componentes premium</p>
                <div className="flex gap-4 mt-4">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">🎯 KPIs</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📱 Responsivo</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">✨ Animações</span>
                </div>
              </div>
              <div className="text-6xl opacity-50 group-hover:opacity-100 transition-opacity">
                →
              </div>
            </div>
          </div>

          {/* Card Executive Dashboard */}
          <div 
            onClick={() => window.location.href = '/executive'}
            className="group bg-gradient-to-r from-gray-800 via-gray-700 to-gray-900 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl cursor-pointer transition-all transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-6xl mb-4">👔</div>
                <h3 className="text-3xl font-bold mb-2">Executive Dashboard</h3>
                <p className="text-xl opacity-90">Visão executiva com métricas estratégicas e relatórios gerenciais</p>
                <div className="flex gap-4 mt-4">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📊 Métricas</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">📈 ROI</span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">🎯 Metas</span>
                </div>
              </div>
              <div className="text-6xl opacity-50 group-hover:opacity-100 transition-opacity">
                →
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {activeModal && (
        <Modal 
          title={
            activeModal === 'users' ? 'Usuários' :
            activeModal === 'forms' ? 'Formulários' :
            activeModal === 'complaints' ? 'Denúncias' : ''
          }
          onClose={() => {
            setActiveModal(null);
            setData(null);
            setError(null);
          }}
        >
          {renderModalContent()}
        </Modal>
      )}

      {/* Estilos de animação */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SuperSimpleDashboard;
